import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Template } from '../../../../../models/Template';
import { TemplateService } from '../../../../../services/template.service';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../../../environments/environment';
import { SafeUrlPipe } from "../../../../../shared/pipes/safe-url.pipe";
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { AlertToast } from '../../../../../shared/notifications/alert.toast';
import { ToastrService } from 'ngx-toastr';
import { CustomDropdownComponent } from '../../../../../shared/components/form-controls/custom-dropdown/custom-dropdown.component';

interface CampoPDF {
  name: string;
  type: string;
  value: string;
  max_length?: string | null;
  justification?: string;
}

@Component({
  selector: 'app-template-details',
  standalone: true,
  imports: [NgIf, NgForOf, FormsModule, PdfViewerModule, CustomDropdownComponent],
  templateUrl: './template-details.component.html',
  styleUrl: './template-details.component.css'
})
export class TemplateDetailsComponent implements OnInit {
  template: Template | null = null;
  templateId: number = 0;
  error: string = '';

  constructor(private activatedRoute: ActivatedRoute, private templateService: TemplateService,  private toastr: ToastrService) {}

  pdfSrc: string = '';

  mapeoCampos: { [fieldName: string]: string } = {};

  camposPDF: CampoPDF[] = [];

  originalMapping: { [fieldName: string]: string } = {};

  toastOptions = {
    toastComponent: AlertToast,
    progressBar: true,
    type: 'success',  // o 'error', 'info', 'warning'
    toastClass: 'toast'
  };
  
  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.templateId = +params['id'];
      if (this.templateId) {
        this.loadTemplate();
        this.pdfSrc = `${environment.API_URL}/template/view/${this.templateId}`;
      }
    });
  }

  loadTemplate(): void {
    this.templateService.getTemplate(this.templateId).subscribe({
      next: (response: any) => {
        console.log('Respuesta completa:', response);
      
        if (response.data) {
          this.template = response.data;
          
          // Si hay campos extraídos en response.data (como array)
          if (Array.isArray(response.data) && response.data.length > 0) {
            this.camposPDF = response.data;
            this.mapeoCampos = {};
            this.camposPDF.forEach(campo => {
              this.mapeoCampos[campo.name] = campo.value || ''; // inicializa vacío
            });
          }
          // Si hay mapping en la plantilla
          else if (response.data.mapping) {
            this.camposPDF = Object.keys(response.data.mapping).map(key => ({
              name: key,
              type: 'text',
              value: response.data.mapping[key],
              max_length: null,
              justification: ''
            }));
            
            // Inicializar el mapeo con los valores existentes
            this.mapeoCampos = { ...response.data.mapping };
            this.originalMapping = { ...response.data.mapping };
          }
          // Si no hay campos, inicializar vacío
          else {
            this.camposPDF = [];
            this.mapeoCampos = {};
            this.originalMapping = {};
            console.warn('No se encontraron campos extraídos en la plantilla');
          }
        }
      },
      error: error => {
        console.error('Error al cargar plantilla:', error);
        this.error = 'Error al cargar la plantilla';
      },
    });
  }

  getPdfUrl(): string {
    return `${environment.API_URL}/template/view/${this.templateId}`;
  }

  guardarMapeo(): void {
    console.log('Mapeo a guardar:', this.mapeoCampos);
    console.log('Mapeo original:', this.originalMapping);
    
    // Limpiar espacios en blanco de las keys
    const cleanedMapping: { [key: string]: string } = {};
    Object.keys(this.mapeoCampos).forEach(key => {
      const value = this.mapeoCampos[key];
      // Si el valor es un espacio en blanco, convertirlo a cadena vacía
      cleanedMapping[key] = (value && value.trim() !== '') ? value : '';
    });
    
    console.log('Mapeo limpio a enviar:', cleanedMapping);
    
    this.templateService.updateTemplate(this.templateId, cleanedMapping).subscribe({
      next: (response: any) => {
        console.log('Plantilla actualizada:', response);
        this.loadTemplate();
      },
      error: error => {
        console.error('Error al actualizar plantilla:', error);
        this.error = 'Error al actualizar la plantilla';
      },
      complete: () => {
        this.toastr.show('Plantilla actualizada correctamente', '¡Éxito!', this.toastOptions);
      }
    });
  }

  extraerCampos(): void {
    console.log('Extrayendo campos del PDF...');
    // Llamar al endpoint para extraer campos
    this.templateService.extractFields(this.templateId).subscribe({
      next: (response: any) => {
        console.log('Campos extraídos:', response);
        this.loadTemplate(); // Recargar para obtener los campos extraídos
      },
      error: error => {
        console.error('Error al extraer campos:', error);
        this.error = 'Error al extraer campos del PDF';
      }
    });
  }

  hasChanges(): boolean {
    return JSON.stringify(this.mapeoCampos) !== JSON.stringify(this.originalMapping);
  }

  getLabelForValue(value: string): string {
    for (const grupo of this.datosDisponibles) {
      const option = grupo.options.find((opt: any) => opt.key === value);
      if (option) {
        return option.label;
      }
    }
    return value;
  }

  // Datos disponibles para el mapeo
  get datosDisponibles(): any[] {
    return [
      {
        label: 'Opciones Especiales',
        options: [
          { key: ' ', label: '-- Vaciar campo --' },
        ]
      },
      {
        label: 'Datos del Usuario',
        options: [
          { key: 'user.name', label: 'Nombre del Usuario' },
          { key: 'user.surname', label: 'Apellidos del Usuario' },
          { key: 'user.full_name', label: 'Nombre completo del Usuario' },
          { key: 'user.nif', label: 'DNI/NIF del Usuario' },
          { key: 'user.address', label: 'Dirección del Usuario' },
          { key: 'user.city', label: 'Ciudad del Usuario' },
          { key: 'user.postal_code', label: 'Código Postal del Usuario' },
          { key: 'user.full_address', label: 'Dirección Completa (Dirección, CP Ciudad)' },
        ]
      },
      {
        label: 'Datos de la empresa',
        options: [
          { key: 'product.office.address', label: '' },
        ]
      },
      {
        label: 'Datos del Producto',
        options: [
          { key: 'product.name', label: 'Nombre del Producto' },
          { key: 'product.price', label: 'Precio del Producto' },
          { key: 'product.description', label: 'Descripción del Producto' },
        ]
      },
      {
        label: 'Datos del Contrato',
        options: [
          { key: 'contract.id', label: 'ID del Contrato' },
          { key: 'contract.contract_number', label: 'Número de Contrato' },
          { key: 'contract.created_at', label: 'Fecha de Creación' },
        ]
      },
      {
        label: 'Fechas',
        options: [
          { key: 'dates.day', label: 'Día' },
          { key: 'dates.month', label: 'Mes' },
          { key: 'dates.year', label: 'Año' },
          { key: 'dates.current_date', label: 'Fecha Actual' },
        ]
      },
      {
        label: 'Datos de la Sucursal/Oficina',
        options: [
          { key: 'office.name', label: 'Nombre de la Oficina' },
          { key: 'office.direccion', label: 'Dirección de la Oficina' },
          { key: 'office.telefono', label: 'Teléfono de la Oficina' },
          { key: 'office.email', label: 'Email de la Oficina' },
        ]
      },
      {
        label: 'Datos de la Empresa/Sociedad',
        options: [
          { key: 'companies.name', label: 'Nombre de la Empresa' },
          { key: 'companies.address', label: 'Dirección de la Empresa' },
          { key: 'companies.city', label: 'Ciudad de la Empresa' },
          { key: 'companies.postal_code', label: 'Código Postal de la Empresa' },
          { key: 'companies.phone', label: 'Teléfono de la Empresa' },
          { key: 'companies.email', label: 'Email de la Empresa' },
        ]
      }
    ];
  }

}
